import { memo, useMemo, useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';
import moment from 'moment';

import { Button } from '@/shadcn/button';
import { Calendar } from '@/shadcn/calendar';
import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/popover';
import { Separator } from '@/shadcn/separator';

import { DEFAULT_INPUT_FORMAT } from '@/components/forms/components/form/reader/widgets/dateWidget';
import type { WidgetEditorBaseProps } from '@/components/ui/widget/views/text/text.view.type';
import { checkIsISODate } from '@/utils/dates';

type Props = WidgetEditorBaseProps<string | undefined> & {
  inputFormat?: string;
  displayFormat?: string;
};

export const DateEditor = memo((props: Props) => {
  const isISODate = checkIsISODate(props.value || '');
  const [open, setOpen] = useState<boolean>(false);
  const [date, setDate] = useState<Maybe<Date>>(undefined);

  const dateToDisplay = useMemo(() => {
    if (!props.value) return '';
    if (props.inputFormat === props.displayFormat) return props.value;
    let momentDate: moment.Moment | null = null;

    if (isISODate) {
      momentDate = moment.utc(props.value);
    } else {
      momentDate = moment.utc(props.value, props.inputFormat, true);
    }

    return momentDate.isValid() ? momentDate.format(props.displayFormat) : '';
  }, [props.value, props.inputFormat, props.displayFormat, isISODate]);

  if (props.disabled) {
    return (
      <div
        className="size-full px-3 py-2 text-sm"
        data-cy={`date-editor-${props.identifier}-disabled`}
      >
        {dateToDisplay}
      </div>
    );
  }

  const isInBatchEdit = !!props.header;

  const onDateChange = (date: Maybe<Date>) => {
    if (!date) return;
    const formatedDate = moment(date).format(
      props.inputFormat || DEFAULT_INPUT_FORMAT
    );
    props.onChange(formatedDate);
    setDate(date);
    setOpen(false);
  };

  const onReset = () => {
    props.onChange(undefined);
    setDate(undefined);
    setOpen(false);
  };

  return (
    <div className={cn('flex flex-col gap-2', !isInBatchEdit && 'size-full')}>
      {isInBatchEdit && (
        <Label
          data-cy={`date-editor-${props.identifier}-label`}
          htmlFor={props.header}
        >
          {props.header}
        </Label>
      )}
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'flex size-full cursor-pointer flex-row items-center justify-between bg-transparent px-3 py-2 text-left font-normal text-sm normal-case hover:bg-accent active:scale-100',
              isInBatchEdit
                ? 'rounded-md border border-input'
                : 'rounded-none border-0 border-transparent focus:ring-0',
              !dateToDisplay && 'text-muted-foreground'
            )}
            data-cy={`date-editor-${props.identifier}-button`}
            id={props.identifier}
            type="button"
          >
            {dateToDisplay || props.placeholder || 'Select a date'}
            <ChevronDownIcon className="size-4 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto overflow-hidden p-0">
          <Calendar
            captionLayout="dropdown"
            data-cy={`date-editor-${props.identifier}-calendar`}
            mode="single"
            onSelect={onDateChange}
            selected={date}
            timeZone="UTC"
          />
          <Separator orientation="horizontal" />
          <Button
            className="m-1 w-full font-normal text-xs normal-case"
            data-cy={`date-editor-${props.identifier}-reset`}
            onClick={onReset}
            variant="ghost"
          >
            Clear
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
});
